import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Brain, Loader2, CheckCircle, XCircle } from "lucide-react";

interface Mcq {
  id: string;
  question: string;
  options: string[];
  correct_index: number;
  explanation?: string;
  topic_id?: string;
  difficulty: number;
}

interface ExplanationGeneratorProps {
  mcq: Mcq;
  isOpen: boolean;
  onClose: () => void;
  topicName?: string;
}

export default function ExplanationGenerator({ 
  mcq, 
  isOpen, 
  onClose, 
  topicName 
}: ExplanationGeneratorProps) {
  const [generatedExplanation, setGeneratedExplanation] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();

  const generateMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('ai-generate-explanation', {
        body: {
          mcqId: mcq.id,
          question: mcq.question,
          options: mcq.options,
          correctIndex: mcq.correct_index,
          topic: topicName,
          difficulty: mcq.difficulty
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setGeneratedExplanation(data.explanation);
      setIsEditing(true);
      toast.success(`Explanation generated! Used ${data.tokensUsed} tokens ($${data.estimatedCost.toFixed(4)})`);
    },
    onError: (error) => {
      console.error('Generation error:', error);
      toast.error('Failed to generate explanation');
    }
  });

  const saveMutation = useMutation({
    mutationFn: async (explanation: string) => {
      const { error } = await supabase
        .from('mcqs')
        .update({ explanation })
        .eq('id', mcq.id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Explanation saved successfully');
      queryClient.invalidateQueries({ queryKey: ['mcqs'] });
      onClose();
    },
    onError: (error) => {
      console.error('Save error:', error);
      toast.error('Failed to save explanation');
    }
  });

  const handleSave = () => {
    if (!generatedExplanation.trim()) {
      toast.error('Please generate or enter an explanation');
      return;
    }
    saveMutation.mutate(generatedExplanation);
  };

  const handleClose = () => {
    setGeneratedExplanation("");
    setIsEditing(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Generate Explanation
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* MCQ Display */}
          <div className="bg-muted/50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Question:</h4>
            <p className="mb-3">{mcq.question}</p>
            
            <h4 className="font-medium mb-2">Options:</h4>
            <div className="space-y-1">
              {mcq.options.map((option, index) => (
                <div 
                  key={index}
                  className={`p-2 rounded ${
                    index === mcq.correct_index 
                      ? 'bg-green-100 border border-green-300 dark:bg-green-900/20 dark:border-green-700' 
                      : 'bg-background'
                  }`}
                >
                  <span className="font-medium">
                    {String.fromCharCode(65 + index)}.
                  </span>{' '}
                  {option}
                  {index === mcq.correct_index && (
                    <CheckCircle className="inline-block ml-2 h-4 w-4 text-green-600" />
                  )}
                </div>
              ))}
            </div>

            {mcq.explanation && (
              <div className="mt-4">
                <h4 className="font-medium mb-2">Current Explanation:</h4>
                <p className="text-sm text-muted-foreground bg-background p-3 rounded">
                  {mcq.explanation}
                </p>
              </div>
            )}
          </div>

          {/* Generation Controls */}
          {!isEditing && (
            <div className="flex gap-2">
              <Button 
                onClick={() => generateMutation.mutate()}
                disabled={generateMutation.isPending}
                className="flex items-center gap-2"
              >
                {generateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Brain className="h-4 w-4" />
                )}
                {generateMutation.isPending ? 'Generating...' : 'Generate Explanation'}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={() => {
                  setGeneratedExplanation(mcq.explanation || "");
                  setIsEditing(true);
                }}
              >
                Edit Manually
              </Button>
            </div>
          )}

          {/* Generated Explanation Editor */}
          {isEditing && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Generated Explanation:
                </label>
                <Textarea
                  value={generatedExplanation}
                  onChange={(e) => setGeneratedExplanation(e.target.value)}
                  rows={12}
                  placeholder="The generated explanation will appear here..."
                  className="resize-none"
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => generateMutation.mutate()}
                  disabled={generateMutation.isPending}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  {generateMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Brain className="h-4 w-4" />
                  )}
                  Regenerate
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={saveMutation.isPending || !generatedExplanation.trim()}
                  className="flex items-center gap-2"
                >
                  {saveMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4" />
                  )}
                  Save Explanation
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}